import React from 'react';
import { Outlet, useNavigate, useLocation, NavLink, useSearchParams } from "react-router-dom";
import { canAccessPath } from "../utils/permissions";
import { useAuth } from "../context/AuthContext";
import {
  SquaresFour, Robot, Gear, SignOut, X,
  CaretLeft, CaretRight, CaretDown, Package, Confetti,
  Clock, User, Users, Target, Bell, WarningCircle, CheckCircle,
  Lightning, Strategy, Warehouse, Skull, Heartbeat, ChartBar,
  CalendarBlank, UserMinus, Briefcase, ClipboardText, GraduationCap, Ruler, ArrowUUpLeft,
} from "@phosphor-icons/react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRAWER_WIDTH = 280;
const SWIPE_OPEN_THRESHOLD = 50;
const SWIPE_CLOSE_THRESHOLD = 50;
const EDGE_ZONE = 22; // px from left edge to detect open swipe

/**
 * FastButton — instant tap on iOS via native touchend with preventDefault.
 * This bypasses the 300ms click delay on all iOS versions.
 * passive:false on touchend is safe (doesn't affect scroll which is controlled by touchmove).
 */
const FastButton = ({ onTap, className, style, children, testId, type = "button" }) => {
  const ref = useRef(null);
  const tapRef = useRef(onTap);
  tapRef.current = onTap; // always up-to-date without re-running effect

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0, startY = 0;

    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
      const dx = Math.abs(e.changedTouches[0].clientX - startX);
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      // Only fire if it was a tap (not a swipe)
      if (dx < 10 && dy < 10) {
        e.preventDefault(); // Prevent the 300ms-delayed click
        tapRef.current?.();
      }
    };

    // passive:true on start (no scroll blocking), passive:false on end (so we can preventDefault)
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []); // Run once. tapRef ensures latest callback is always called.

  return (
    <button
      ref={ref}
      type={type}
      onClick={tapRef.current} // Desktop / mouse fallback
      className={className}
      style={{
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        userSelect: "none",
        outline: "none",
        ...style,
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
};

const Layout = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({sales: true, stock: true, procurement: true, people: true, market: true});
  // For restricted-category users the sidebar has ZERO top-level items
  // (Brief / Pulse / War Room / Synapse / Review / Settings all filtered out).
  // In that case, auto-expand every section group ONCE so users can actually
  // see their allowed pages. Admin users always have top-level items so this
  // effect never fires for them — their default-collapsed behavior is preserved.
  const restrictedAutoExpandDone = useRef(false);
  const [alertCount, setAlertCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  // 0 = fully closed, 1 = fully open (Twitter-style fraction)
  const [fraction, setFraction] = useState(0);

  const edgeRef = useRef(null);
  const drawerRef = useRef(null);
  // Refs so gesture closures always see latest state
  const fractionRef = useRef(fraction);
  fractionRef.current = fraction;
  const menuOpenRef = useRef(mobileMenuOpen);
  menuOpenRef.current = mobileMenuOpen;

  useEffect(() => {
    const saved = localStorage.getItem("sidebar_collapsed");
    if (saved !== null) setSidebarCollapsed(JSON.parse(saved));
  }, []);

  // Auto-expand the section containing the current page
  useEffect(() => {
    const path = location.pathname;
    const sectionMap = {
      sales: salesItems.map(i => i.path),
      stock: stockItems.map(i => i.path),
      procurement: procurementItems.map(i => i.path),
      people: peopleItems.map(i => i.path),
      market: marketItems.map(i => i.path),
    };
    for (const [key, paths] of Object.entries(sectionMap)) {
      if (paths.some(p => path.startsWith(p) || path === p.split('/').slice(0, -1).join('/'))) {
        setCollapsedSections(prev => ({...prev, [key]: false}));
        break;
      }
    }
  }, [location.pathname]);


  // Fetch anomaly alerts count for bell icon
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/alerts/anomalies`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const items = res.data?.anomalies || [];
        setAlerts(items);
        setAlertCount(items.length);
      })
      .catch(() => {});
  }, [token]);

  // Close drawer on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setFraction(0);
  }, [location.pathname]);

  // Snap fraction to 0 or 1 when mobileMenuOpen changes (not during drag)
  useEffect(() => {
    setFraction(mobileMenuOpen ? 1 : 0);
  }, [mobileMenuOpen]);

  // ── EDGE STRIP: swipe right from left edge → open drawer ──────────────────
  useEffect(() => {
    const el = edgeRef.current;
    if (!el) return;

    let sx = 0, sy = 0, cx = 0;

    const onStart = (e) => {
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      cx = sx;
    };
    const onMove = (e) => {
      cx = e.touches[0].clientX;
      const dx = cx - sx;
      const dy = Math.abs(e.touches[0].clientY - sy);
      if (dx > 0 && dy < Math.abs(dx) * 2) {
        setFraction(Math.min(dx / DRAWER_WIDTH, 1));
      }
    };
    const onEnd = () => {
      const dx = cx - sx;
      if (dx > SWIPE_OPEN_THRESHOLD) {
        setMobileMenuOpen(true);
      } else {
        setFraction(menuOpenRef.current ? 1 : 0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, []);

  // ── DRAWER PANEL: swipe left → close drawer ────────────────────────────────
  useEffect(() => {
    const el = drawerRef.current;
    if (!el) return;

    let sx = 0, sy = 0, cx = 0;

    const onStart = (e) => {
      if (!menuOpenRef.current) return;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      cx = sx;
    };
    const onMove = (e) => {
      if (!menuOpenRef.current) return;
      cx = e.touches[0].clientX;
      const dx = cx - sx;
      const dy = Math.abs(e.touches[0].clientY - sy);
      if (dx < 0 && dy < Math.abs(dx) * 2) {
        setFraction(Math.max(0, 1 + dx / DRAWER_WIDTH));
      }
    };
    const onEnd = () => {
      if (!menuOpenRef.current) return;
      const dx = cx - sx;
      if (dx < -SWIPE_CLOSE_THRESHOLD) {
        setMobileMenuOpen(false);
      } else {
        setFraction(1);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, []);

  const isDragging = fraction > 0 && fraction < 1;
  const transition = isDragging ? "none" : "transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)";
  const drawerTransform = `translateX(${(fraction - 1) * DRAWER_WIDTH}px)`;
  const mainTransform = `translateX(${fraction * DRAWER_WIDTH}px)`;

  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem("sidebar_collapsed", JSON.stringify(next));
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  // Permission-based nav filtering
  const perms = user?.permissions || [];
  const canSee = (path) => canAccessPath(perms, path);
  const filterList = (items) => items.filter(it => canSee(it.path));

  // Desktop sidebar — top-level items (includes Review Centre)
  // Settings is intentionally NOT here so it can be rendered at the bottom
  // of the sidebar (below every collapsible section) — see settingsItem below.
  const navItemsAll = [
    { path: "/brief", icon: SquaresFour, label: "Brief" },
    { path: "/pulse", icon: Lightning, label: "Pulse" },
    { path: "/warroom", icon: Strategy, label: "War Room" },
    { path: "/ask", icon: Robot, label: "Synapse" },
    { path: "/review", icon: ClipboardText, label: "Review" },
    { path: "/space-intel", icon: Ruler, label: "Space Intel" },
  ];
  const navItems = filterList(navItemsAll);
  const settingsItem = filterList([{ path: "/settings", icon: Gear, label: "Settings" }])[0];

  // Restricted "Simple Dashboards" — top-level sidebar entries for the
  // category's granted dashboards. When a non-admin user has any granted
  // dashboards, we treat them as a Simple-Dashboards user and hide the
  // ENTIRE admin sidebar so they only see their allowed tiles + Sign Out.
  //
  // Admin Preview mode also uses the SAME simplified sidebar so admins
  // see exactly what a restricted user would see when previewing a
  // category — driven by the previewed category's `dashboards`, not the
  // admin's own permissions.
  const scopedDashboardsAll = [
    { path: "/scoped/overall", icon: SquaresFour, label: "Overall" },
    { path: "/scoped/sales", icon: ChartBar, label: "Sales" },
    { path: "/scoped/stocks", icon: Warehouse, label: "Stocks" },
  ];

  const [searchParams] = useSearchParams();
  const previewCategoryId = location.pathname.startsWith("/scoped/") ? searchParams.get("preview") : null;
  const [previewCategory, setPreviewCategory] = useState(null);

  useEffect(() => {
    if (!previewCategoryId || !user) {
      setPreviewCategory(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/scoped-dashboards/scope?category_id=${previewCategoryId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('crpl_token')}` },
      })
      .then((r) => {
        if (!cancelled) setPreviewCategory(r.data);
      })
      .catch(() => {
        if (!cancelled) setPreviewCategory(null);
      });
    return () => { cancelled = true; };
  }, [previewCategoryId, user]);

  const grantedDashboards = previewCategoryId
    ? (previewCategory?.dashboards || [])
    : (user?.dashboards || []);

  // Simple-Dashboards mode kicks in when either:
  //   (a) the logged-in user's category IS a restricted one, or
  //   (b) an admin is actively previewing a restricted category.
  const isSimpleDashboardsUser =
    (user && user.is_admin_category === false && (user.dashboards || []).length > 0) ||
    Boolean(previewCategoryId);

  const scopedItems = isSimpleDashboardsUser
    ? scopedDashboardsAll
        .filter((d) => grantedDashboards.includes(d.path.split("/").pop()))
        .map((d) => ({
          ...d,
          // In preview mode, preserve `?preview=<id>` when clicking between
          // the previewed category's dashboards so the admin can switch
          // tiles without exiting preview.
          path: previewCategoryId ? `${d.path}?preview=${previewCategoryId}` : d.path,
        }))
    : [];

  // Mobile bottom nav — 5 tabs only (no Review; Review accessible via slide-out drawer)
  const mobileBottomItems = navItems.filter((it) => it.path !== "/review");

  const salesItems = filterList([
    { path: "/analysis/departments", icon: Package, label: "Departments" },
    { path: "/analysis/festivals", icon: Confetti, label: "Festivals" },
    { path: "/analysis/peak-hours", icon: Clock, label: "Peak Hours" },
    { path: "/analysis/salesperson", icon: User, label: "Salesperson" },
    { path: "/analysis/vendors", icon: Users, label: "Vendors" },
    { path: "/analysis/product-intel", icon: Lightning, label: "Product Intel" },
    { path: "/analysis/returns", icon: ArrowUUpLeft, label: "Returns" },
  ]);

  const stockItems = filterList([
    { path: "/analysis/stock", icon: Warehouse, label: "Stock Intel" },
    { path: "/analysis/item-run-rate", icon: ChartBar, label: "Item Run Rate" },
    { path: "/analysis/product-deep-dive", icon: Package, label: "Product Dive" },
    { path: "/analysis/dead-stock-recovery", icon: Skull, label: "Recovery" },
    { path: "/analysis/vendor-scorecard", icon: Target, label: "Vendor Score" },
  ]);

  const procurementItems = filterList([
    { path: "/procurement", icon: Package, label: "Purchase Intel" },
    { path: "/procurement/vendor-360", icon: Users, label: "Vendor 360" },
    { path: "/procurement/festival-buying", icon: Package, label: "Festival Buying" },
    { path: "/procurement/logistics", icon: Target, label: "Logistics" },
  ]);

  const peopleItems = filterList([
    { path: "/hr/overview", icon: Heartbeat, label: "HR Overview" },
    { path: "/hr/attendance", icon: CalendarBlank, label: "Attendance" },
    { path: "/hr/attrition", icon: UserMinus, label: "Attrition" },
    { path: "/hr/performance", icon: Target, label: "Performance" },
    { path: "/hr/workforce", icon: Briefcase, label: "Workforce" },
    { path: "/hr/demographics", icon: Users, label: "Demographics" },
    { path: "/hr/interns", icon: GraduationCap, label: "Interns" },
    { path: "/hr/ideal-employee", icon: Lightning, label: "Employee DNA" },
  ]);

  const marketItems = filterList([
    { path: "/analysis/market-intel", icon: Target, label: "Market Intel" },
  ]);

  // Auto-expand section groups for restricted-category users (no top-level nav
  // items visible). Runs once per user session so a manual toggle sticks.
  // Simple-Dashboards users don't need this — they have their own top-level
  // scoped items — so we skip when scopedItems is populated.
  useEffect(() => {
    if (
      !restrictedAutoExpandDone.current &&
      perms.length > 0 &&
      navItems.length === 0 &&
      scopedItems.length === 0
    ) {
      setCollapsedSections({
        sales: false, stock: false, procurement: false, people: false, market: false,
      });
      restrictedAutoExpandDone.current = true;
    }
  }, [perms.length, navItems.length, scopedItems.length]);

  return (
    <div className="flex overflow-hidden bg-[#FAFAFA]" style={{ height: '100dvh' }}>

      {/* ═══════════════════════════════════════════════
          DESKTOP SIDEBAR (unchanged)
      ═══════════════════════════════════════════════ */}
      <aside
        className={`hidden lg:flex flex-col ${sidebarCollapsed ? "w-20" : "w-64"} bg-gradient-to-b from-[#1a1f1c] to-[#0f1210] text-white transition-all duration-300 flex-shrink-0`}
      >
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white p-1.5 flex items-center justify-center shadow-lg flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_chandan-sales-ai/artifacts/tu9ehfss_askaran_binraj-removebg-preview.png"
              alt="CRPL Logo"
              className="w-full h-full object-contain"
            />
          </div>
          {!sidebarCollapsed && (
            <div>
              <h1 className="font-bold text-base font-['Outfit'] text-white">CRPL PULSE</h1>
              <p className="text-xs text-emerald-400/80 font-medium">Retail Analytics</p>
            </div>
          )}
        </div>
        <button
          onClick={toggleSidebar}
          className="mx-auto mb-2 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          {sidebarCollapsed ? <CaretRight size={16} className="text-white" /> : <CaretLeft size={16} className="text-white" />}
        </button>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {scopedItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.split('?')[0].replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  isActive
                    ? "bg-[#1B5E20] text-white shadow-md shadow-green-900/30"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                } ${sidebarCollapsed ? "justify-center" : ""}`
              }
            >
              <item.icon size={22} weight="duotone" />
              {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
            </NavLink>
          ))}
          {!isSimpleDashboardsUser && navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  isActive
                    ? "bg-[#1B5E20] text-white shadow-md shadow-green-900/30"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                } ${sidebarCollapsed ? "justify-center" : ""}`
              }
            >
              <item.icon size={22} weight="duotone" />
              {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
            </NavLink>
          ))}
          {!isSimpleDashboardsUser && [
            { label: 'Sales Analytics', key: 'sales', items: salesItems },
            { label: 'Stock Intelligence', key: 'stock', items: stockItems },
            { label: 'Procurement', key: 'procurement', items: procurementItems },
            { label: 'People Intel', key: 'people', items: peopleItems },
            { label: 'Market', key: 'market', items: marketItems },
          ].filter(s => s.items.length > 0).map(section => (
            <React.Fragment key={section.key}>
              {!sidebarCollapsed ? (
                <div className="pt-4 pb-1">
                  <button onClick={() => setCollapsedSections(prev => ({...prev, [section.key]: !prev[section.key]}))}
                    className="w-full flex items-center justify-between px-3 py-1.5 group hover:bg-white/5 rounded-lg transition-colors">
                    <p className="text-[11px] font-bold text-white/40 uppercase tracking-widest group-hover:text-white/60">{section.label}</p>
                    <CaretDown size={12} weight="bold" className={`text-white/30 transition-transform ${collapsedSections[section.key] ? '-rotate-90' : ''}`} />
                  </button>
                </div>
              ) : <div className="border-t border-white/10 my-2" />}
              {!collapsedSections[section.key] && section.items.map((item) => (
                <NavLink key={item.path} to={item.path} end
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                      isActive ? "bg-[#1B5E20] text-white shadow-md shadow-green-900/30" : "text-white/60 hover:text-white hover:bg-white/10"
                    } ${sidebarCollapsed ? "justify-center" : ""}`
                  }
                >
                  <item.icon size={22} weight="duotone" />
                  {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
          {/* Settings — pinned below all sections. Hidden for Simple-Dashboards users. */}
          {settingsItem && !isSimpleDashboardsUser && (
            <div className="pt-4">
              {!sidebarCollapsed && <div className="border-t border-white/10 mb-2" />}
              <NavLink to={settingsItem.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive ? "bg-[#1B5E20] text-white shadow-md shadow-green-900/30" : "text-white/60 hover:text-white hover:bg-white/10"
                  } ${sidebarCollapsed ? "justify-center" : ""}`
                }
              >
                <settingsItem.icon size={22} weight="duotone" />
                {!sidebarCollapsed && <span className="font-medium text-sm">{settingsItem.label}</span>}
              </NavLink>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl w-full text-white/60 hover:text-white hover:bg-red-500/10 transition-all ${sidebarCollapsed ? "justify-center" : ""}`}
          >
            <SignOut size={22} weight="duotone" />
            {!sidebarCollapsed && <span className="font-medium text-sm">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* ═══════════════════════════════════════════════
          DESKTOP CONTENT AREA
      ═══════════════════════════════════════════════ */}
      <div className="hidden lg:flex flex-1 flex-col overflow-y-auto">
        <Outlet />
      </div>

      {/* ═══════════════════════════════════════════════
          MOBILE LAYOUT — Twitter-style push drawer
      ═══════════════════════════════════════════════ */}
      <div className="lg:hidden relative flex-1 overflow-hidden">

        {/* ── DRAWER PANEL (slides in from left) ─────────────────── */}
        <div
          ref={drawerRef}
          className="absolute left-0 top-0 bottom-0 bg-[#0f1210] flex flex-col z-20"
          style={{
            width: DRAWER_WIDTH,
            transform: drawerTransform,
            transition,
            willChange: "transform",
          }}
        >
          {/* Profile header */}
          <div className="p-5 pb-4 border-b border-white/8" style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-[#1B5E20] flex items-center justify-center text-white font-bold text-lg ring-2 ring-emerald-400/20">
                {user?.name?.charAt(0) || "U"}
              </div>
              <FastButton
                onTap={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-full text-white/40 active:text-white active:bg-white/10"
              >
                <X size={20} />
              </FastButton>
            </div>
            <p className="text-white font-semibold text-base">{user?.name}</p>
            <p className="text-sm text-white/40 mt-0.5">{user?.role || "CEO"} · CRPL</p>
          </div>

          {/* Nav items */}
          <div className="flex-1 overflow-y-auto py-2">
            <div className="px-3 space-y-0.5">
              {scopedItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <FastButton
                    key={item.path}
                    onTap={() => { setMobileMenuOpen(false); navigate(item.path); }}
                    className={`flex items-center gap-3.5 px-4 py-3 rounded-xl w-full text-left ${
                      isActive ? "bg-[#1B5E20]/80 text-white" : "text-white/60 active:text-white active:bg-white/5"
                    }`}
                    style={{ background: "none", border: "none" }}
                  >
                    <item.icon size={22} weight="duotone" />
                    <span className="font-medium text-[15px]">{item.label}</span>
                  </FastButton>
                );
              })}
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <FastButton
                    key={item.path}
                    onTap={() => { setMobileMenuOpen(false); navigate(item.path); }}
                    className={`flex items-center gap-3.5 px-4 py-3 rounded-xl w-full text-left ${
                      isActive ? "bg-[#1B5E20]/80 text-white" : "text-white/60 active:text-white active:bg-white/5"
                    }`}
                    style={{ background: "none", border: "none" }}
                  >
                    <item.icon size={22} weight="duotone" />
                    <span className="font-medium text-[15px]">{item.label}</span>
                  </FastButton>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-white/6">
              {!isSimpleDashboardsUser && [
                { label: 'Sales Analytics', key: 'sales', items: salesItems },
                { label: 'Stock Intelligence', key: 'stock', items: stockItems },
                { label: 'Procurement', key: 'procurement', items: procurementItems },
                { label: 'People Intel', key: 'people', items: peopleItems },
                { label: 'Market', key: 'market', items: marketItems },
              ].filter(s => s.items.length > 0).map(section => (
                <div key={section.key} className="mb-3">
                  <button onClick={() => setCollapsedSections(prev => ({...prev, [section.key]: !prev[section.key]}))}
                    className="w-full flex items-center justify-between px-7 mb-1 py-1 group active:bg-white/5 rounded-lg">
                    <p className="text-[11px] font-bold text-white/40 uppercase tracking-[0.15em]">{section.label}</p>
                    <CaretDown size={12} weight="bold" className={`text-white/30 mr-3 transition-transform ${collapsedSections[section.key] ? '-rotate-90' : ''}`} />
                  </button>
                  {!collapsedSections[section.key] && (
                    <div className="px-3 space-y-0.5">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <FastButton
                            key={item.path}
                            onTap={() => { setMobileMenuOpen(false); navigate(item.path); }}
                            className={`flex items-center gap-3.5 px-4 py-2.5 rounded-xl w-full text-left ${
                              isActive ? "bg-[#1B5E20]/80 text-white" : "text-white/60 active:text-white active:bg-white/5"
                            }`}
                            style={{ background: "none", border: "none" }}
                          >
                            <item.icon size={20} weight="duotone" />
                            <span className="font-medium text-sm">{item.label}</span>
                          </FastButton>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {/* Settings — pinned below all sections in mobile drawer too. Hidden for Simple-Dashboards users. */}
              {settingsItem && !isSimpleDashboardsUser && (
                <div className="mt-3 pt-3 border-t border-white/6">
                  <div className="px-3">
                    <FastButton
                      onTap={() => { setMobileMenuOpen(false); navigate(settingsItem.path); }}
                      className={`flex items-center gap-3.5 px-4 py-3 rounded-xl w-full text-left ${
                        location.pathname === settingsItem.path ? "bg-[#1B5E20]/80 text-white" : "text-white/60 active:text-white active:bg-white/5"
                      }`}
                      style={{ background: "none", border: "none" }}
                    >
                      <settingsItem.icon size={22} weight="duotone" />
                      <span className="font-medium text-[15px]">{settingsItem.label}</span>
                    </FastButton>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-white/6">
            <FastButton
              onTap={handleLogout}
              className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-white/50 active:text-white active:bg-red-500/10"
              style={{ background: "none", border: "none" }}
            >
              <SignOut size={20} weight="duotone" />
              <span className="font-medium text-sm">Sign Out</span>
            </FastButton>
          </div>
        </div>

        {/* ── MAIN CONTENT (slides right when drawer opens) ──────── */}
        <main
          className="absolute inset-0 flex flex-col bg-[#FAFAFA] z-10"
          style={{
            transform: mainTransform,
            transition,
            willChange: "transform",
          }}
        >
          {/* Header — hidden in landscape for immersive content */}
          <header
            className="landscape:hidden flex items-center justify-between px-4 bg-gradient-to-r from-[#1a1f1c] to-[#0f1210] border-b border-white/10 flex-shrink-0"
            style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))", paddingBottom: "0.5rem" }}
          >
            <FastButton
              onTap={() => setMobileMenuOpen(true)}
              className="flex items-center gap-2"
              style={{ background: "none", border: "none" }}
              testId="mobile-avatar-menu"
            >
              <div className="w-8 h-8 landscape:w-7 landscape:h-7 rounded-full bg-[#1B5E20] flex items-center justify-center text-white font-bold text-sm ring-2 ring-emerald-400/30">
                {user?.name?.charAt(0) || "U"}
              </div>
              <div>
                <h1 className="font-bold text-sm font-['Outfit'] text-white leading-tight">CRPL PULSE</h1>
                <p className="text-[10px] text-emerald-400/80 font-medium landscape:hidden">Retail Analytics</p>
              </div>
            </FastButton>
            <div className="flex items-center gap-3">
              {/* Bell icon with anomaly count */}
              <FastButton
                onTap={() => { setShowAlerts(true); setAlertCount(0); }}
                testId="alerts-bell-btn"
                className="relative p-1.5"
                style={{ background: "none", border: "none" }}
              >
                <Bell size={22} className="text-white/70" />
                {alertCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold px-0.5">
                    {alertCount > 9 ? "9+" : alertCount}
                  </span>
                )}
              </FastButton>
              <div className="w-8 h-8 landscape:w-7 landscape:h-7 rounded-lg bg-white p-1 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_chandan-sales-ai/artifacts/tu9ehfss_askaran_binraj-removebg-preview.png"
                  alt="CRPL"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </header>

          {/* Alerts slide-up panel */}
          {showAlerts && (
            <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={() => setShowAlerts(false)}>
              <div
                className="bg-[#0f1210] rounded-t-2xl max-h-[75vh] flex flex-col shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <Bell size={18} className="text-emerald-400" />
                    <h3 className="text-white font-semibold text-base">Smart Alerts</h3>
                    {alertCount > 0 && (
                      <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2 py-0.5 rounded-full">{alertCount}</span>
                    )}
                  </div>
                  <FastButton onTap={() => setShowAlerts(false)} style={{ background: "none", border: "none" }} className="text-white/40 p-1">
                    <X size={20} />
                  </FastButton>
                </div>
                <div className="overflow-y-auto px-4 py-3 space-y-3">
                  {alerts.length === 0 ? (
                    <div className="py-8 text-center">
                      <CheckCircle size={32} className="text-emerald-400 mx-auto mb-3" />
                      <p className="text-white/60 text-sm">All KPIs are within normal range</p>
                    </div>
                  ) : (
                    alerts.map((alert, i) => (
                      <div key={i} className={`rounded-xl p-3.5 border ${
                        alert.severity === 'HIGH' || alert.severity === 'CRITICAL'
                          ? 'bg-red-500/10 border-red-500/20'
                          : 'bg-amber-500/10 border-amber-500/20'
                      }`}>
                        <div className="flex items-start gap-2.5">
                          <WarningCircle size={16} className={alert.severity === 'HIGH' || alert.severity === 'CRITICAL' ? 'text-red-400 mt-0.5 flex-shrink-0' : 'text-amber-400 mt-0.5 flex-shrink-0'} />
                          <div>
                            <p className="text-white text-sm font-medium leading-snug">{alert.message || alert.title}</p>
                            {alert.detail && <p className="text-white/50 text-xs mt-1">{alert.detail}</p>}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Page content */}
          <div
            className={`flex-1 ${location.pathname === "/ask" ? "overflow-hidden flex flex-col" : "overflow-y-auto pb-16 landscape:pb-0"}`}
          >
            <Outlet />
          </div>

          {/* Dim overlay — tapping this area closes the drawer */}
          {fraction > 0 && (
            <div
              className="absolute inset-0 z-30"
              style={{ backgroundColor: `rgba(0,0,0,${fraction * 0.45})`, pointerEvents: fraction > 0.1 ? "auto" : "none" }}
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Bottom nav — hidden in landscape, flex child on Synapse, fixed on other pages */}
          <nav
            className={`landscape:hidden ${location.pathname === "/ask" ? "flex-shrink-0" : "fixed bottom-0 left-0 right-0"} bg-[#0f1210]/95 backdrop-blur-xl border-t border-white/5 z-20`}
            data-testid="mobile-bottom-nav"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="flex items-center justify-around py-1.5 px-2">
              {mobileBottomItems.map((item) => {
                const isActive = location.pathname === item.path || (item.path === "/brief" && location.pathname === "/");
                return (
                  <FastButton
                    key={item.path}
                    onTap={() => navigate(item.path)}
                    testId={`mobile-nav-${item.label.toLowerCase()}`}
                    className="flex flex-col items-center gap-0.5 px-6 py-1.5 rounded-2xl relative"
                    style={{ background: "none", border: "none" }}
                  >
                    {isActive && <div className="absolute -top-1.5 w-8 h-1 rounded-full bg-[#4CAF50]" />}
                    <item.icon
                      size={24}
                      weight={isActive ? "fill" : "regular"}
                      className={isActive ? "text-[#4CAF50]" : "text-white/40"}
                    />
                    <span className={`text-[10px] font-semibold whitespace-nowrap ${isActive ? "text-[#4CAF50]" : "text-white/40"}`}>
                      {item.label}
                    </span>
                  </FastButton>
                );
              })}
            </div>
          </nav>

          {/* Edge strip — 22px left zone to detect swipe-to-open */}
          <div
            ref={edgeRef}
            className="absolute left-0 top-0 bottom-0 z-40"
            style={{ width: EDGE_ZONE, touchAction: "pan-y" }}
          />
        </main>
      </div>
    </div>
  );
};

export default Layout;